import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { navItemsData, siteInfoData } from '../data/navItems';
import SocialIcon from './common/SocialIcon';

const Navbar = ({ currentPage }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
    const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
    const productDropdownTimer = useRef(null);
    const moreDropdownTimer = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const productCategories = [
        { name: "Instrumentation & Automation", page: "products/instrumentation" },
        { name: "Electrical", page: "products/electrical" },
        { name: "Wireless", page: "products/wireless" },
        { name: "Laboratory", page: "products/laboratory" },
        { name: "Display", page: "products/display" },
        { name: "Microwave Moisture Sensors", page: "products/microwave-moisture-sensors" },
        { name: "X-Ray", page: "products/x-ray" },
        { name: "NIR", page: "products/nir" }
    ];

    const moreMenuItems = [
        { name: "About Us", page: "about" },
        { name: "Knowledge Base", page: "knowledge" },
        { name: "News", page: "news" },
    ];

    const handleProductMouseEnter = () => {
        if (productDropdownTimer.current) {
            clearTimeout(productDropdownTimer.current);
        }
        setIsProductDropdownOpen(true);
    };

    const handleProductMouseLeave = () => {
        productDropdownTimer.current = setTimeout(() => {
            setIsProductDropdownOpen(false);
        }, 200); // 200ms delay before closing
    };

    const handleMoreMouseEnter = () => {
        if (moreDropdownTimer.current) {
            clearTimeout(moreDropdownTimer.current);
        }
        setIsMoreDropdownOpen(true);
    };

    const handleMoreMouseLeave = () => {
        moreDropdownTimer.current = setTimeout(() => {
            setIsMoreDropdownOpen(false);
        }, 200); // 200ms delay before closing
    };

    // Cleanup timers on component unmount
    useEffect(() => {
        return () => {
            if (productDropdownTimer.current) {
                clearTimeout(productDropdownTimer.current);
            }
            if (moreDropdownTimer.current) {
                clearTimeout(moreDropdownTimer.current);
            }
        };
    }, []);

    return (
        <div className="sticky top-0 z-50 shadow-lg">
            {/* Top Bar */}
            <div className="bg-gray-800 text-gray-300 py-1">
                <div className="container mx-auto px-4  sm:px-6 lg:px-8 h-10 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        {siteInfoData.socialLinks.map(social => (
                            <SocialIcon key={social.name} char={social.char} url={social.url} />
                        ))}
                    </div>
                    <div 
                        className="text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px] sm:max-w-none ml-2"
                        dangerouslySetInnerHTML={{ __html: siteInfoData.tagline }}
                    />
                </div>
            </div>

            {/* Main Navigation Bar */}
            <nav className="bg-white text-gray-800">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        <div className="flex-shrink-0">
                            <Link 
                                to="/" 
                                className="hover:opacity-80 transition-opacity duration-300"
                            >
                                <img 
                                    src="/logo.png" 
                                    alt="Jiotronik Logo" 
                                    className="h-8 w-auto"
                                />
                            </Link>
                        </div>
                        <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
                            {navItemsData.map((item) => {
                                if (item.page === 'products') {
                                    return (
                                        <div key={item.name} className="relative">
                                            <button
                                                onMouseEnter={handleProductMouseEnter}
                                                onMouseLeave={handleProductMouseLeave}
                                                onClick={() => {navigate('/products'); setIsProductDropdownOpen(false);}}
                                                className={`px-3 py-2 rounded-md text-md font-medium transition-colors duration-300 flex items-center
                                                    ${location.pathname.startsWith('/products') 
                                                        ? 'bg-yellow-500 text-black' 
                                                        : 'text-gray-600 hover:bg-gray-100 hover:text-yellow-600'
                                                    }`}
                                            >
                                                {item.name}
                                                <svg className={`w-4 h-4 ml-1 transition-transform duration-200 ${isProductDropdownOpen ? 'transform rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                                            </button>
                                            {isProductDropdownOpen && (
                                                <div 
                                                    onMouseEnter={handleProductMouseEnter}
                                                    onMouseLeave={handleProductMouseLeave}
                                                    className="absolute left-0 mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 py-1 z-20"
                                                >
                                                    {productCategories.map(category => (
                                                        <button
                                                            key={category.name}
                                                            onClick={() => {
                                                                navigate(`/${category.page}`);
                                                                setIsProductDropdownOpen(false);
                                                            }}
                                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-yellow-600"
                                                        >
                                                            {category.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                if (item.page === 'more_dropdown_placeholder') {
                                    return (
                                        <div key={item.name} className="relative">
                                            <button
                                                onMouseEnter={handleMoreMouseEnter}
                                                onMouseLeave={handleMoreMouseLeave}
                                                className={`px-3 py-2 rounded-md text-md font-medium transition-colors duration-300 flex items-center text-gray-600 hover:bg-gray-100 hover:text-yellow-600`}
                                            >
                                                {item.name}
                                                <svg className={`w-4 h-4 ml-1 transition-transform duration-200 ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                                            </button>
                                            {isMoreDropdownOpen && (
                                                <div 
                                                    onMouseEnter={handleMoreMouseEnter}
                                                    onMouseLeave={handleMoreMouseLeave}
                                                    className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 py-1 z-20"
                                                >
                                                    {moreMenuItems.map(subItem => (
                                                        <button
                                                            key={subItem.name}
                                                            onClick={() => {
                                                                navigate(`/${subItem.page}`);
                                                                setIsMoreDropdownOpen(false);
                                                            }}
                                                            className="block w-full text-left px-4 py-2 text-md text-gray-700 hover:bg-gray-100 hover:text-yellow-600"
                                                        >
                                                            {subItem.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <Link
                                        key={item.name}
                                        to={`/${item.page === 'home' ? '' : item.page}`}
                                        className={`px-3 py-2 rounded-md text-md font-medium transition-colors duration-300
                                            ${location.pathname === `/${item.page === 'home' ? '' : item.page}` 
                                                ? 'bg-yellow-500 text-black' 
                                                : 'text-gray-600 hover:bg-gray-100 hover:text-yellow-600'
                                            }`}
                                    >
                                        {item.name}
                                    </Link>
                                );
                            })}
                        </div>
                        <div className="hidden md:flex items-center ml-6">
                            <div className="flex items-center  bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-5 py-2.5 rounded-md">
                                <svg className="w-8 h-8 text-gray-700 mr-2" fill="currentColor" viewBox="0 0 20 20"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path></svg>
                                <div className="text-left">
                                    <span className="block text-xs text-gray-600">Get Consultation</span>
                                    <a href={`tel:${siteInfoData.contactPhoneNumber.replace(/[^\d+]/g, '')}`} className="block text-sm font-semibold text-gray-700 hover:text-gray-800">{siteInfoData.contactPhoneNumber}</a>
                                </div>
                            </div>
                            {/* <Link 
                                to="/contact"
                                className="hidden lg:block ml-6 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-5 py-2.5 rounded-md text-sm shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                                Get Consultation
                            </Link> */}
                        </div>
                        <div className="md:hidden">
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-yellow-500 transition-all"
                            >
                                <span className="sr-only">Open main menu</span>
                                {isMobileMenuOpen ? (
                                    <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                    {isMobileMenuOpen && (
                        <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg z-40 border-t border-gray-200">
                            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                                {navItemsData.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={`/${item.page === 'home' ? '' : item.page}`}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors duration-300
                                            ${location.pathname === `/${item.page === 'home' ? '' : item.page}` 
                                                ? 'bg-yellow-500 text-black' 
                                                : 'text-gray-700 hover:bg-gray-100 hover:text-yellow-600'
                                            }`}
                                    >
                                        {item.name}
                                    </Link>
                                ))}
                                <Link 
                                    to="/contact"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="mt-2 block w-full text-center bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-4 py-3 rounded-md text-base shadow-md hover:shadow-lg transition-all duration-300"
                                >
                                    Get Consultation
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </nav>
        </div>
    );
};

export default Navbar;